import React, { useState, useEffect, useContext, useMemo } from "react"
import { get, pick, isEmpty } from "lodash"
import ClassNames from "classnames"

import { rowTable } from "config/table_columns"
import { RowTable } from "components/molecules/table"
import { APIResponse } from "redux/apis/types"
import * as Formatter from "utils/Formatter"
import { PieChart } from "components/atoms/chart/pieChart"
import { checkIsSp, judgingKakuhoFlag } from "utils/util"
import { SymbolDetailContext } from "components/pages/others/symbolDetail"

import APIs from "redux/apis"
import { useSelector } from "react-redux"
import store from "redux/store"
import { refreshSymbol } from "redux/ducks/SymbolDetail"
import uuid from "uuid"
import { pageLoadRegist } from "../../../../redux/ducks/PageLoad"

type TrustType = {
  symbolDetail: APIResponse.Res
}
type ChartLabelType = Array<{
  data: string
  name: string
  color: string
  ippan: boolean
}>
export const Trust: React.FC<TrustType> = props => {
  const [symbolData, setSymbolData] = useState({})
  const targetData = get(props.symbolDetail, "Symbol", {})
  const loadingId = useMemo(() => uuid(), [])
  const isRefresh = useSelector(
    (state: { symbolDetail: { refresh: boolean } }) =>
      state.symbolDetail.refresh
  )
  const chartLabel: ChartLabelType = [
    {
      data: "IppanUriZan",
      name: "一般売残",
      color: "#4CC4FF",
      ippan: true,
    },
    { data: "SeidoUriZan", name: "制度売残", color: "#6A85ED", ippan: false },
    { data: "IppanKaiZan", name: "一般買残", color: "#FF639A", ippan: true },
    { data: "SeidoKaiZan", name: "制度買残", color: "#F00054", ippan: false },
  ]

  const isSp = checkIsSp()

  const ippanLabel: ChartLabelType = chartLabel.filter(lst => lst.ippan)
  const seidoLabel: ChartLabelType = chartLabel.filter(lst => !lst.ippan)

  const symbolDetailContext = useContext(SymbolDetailContext)
  const { flag, setFlag } = symbolDetailContext

  const dataList = (lst: ChartLabelType) =>
    lst.map(lst => get(symbolData, lst.data))
  const labelList = (lst: ChartLabelType) => lst.map(lst => lst.name)
  const colorList = (lst: ChartLabelType) => lst.map(lst => lst.color)
  const chartInfo = [
    {
      data: dataList(chartLabel),
      label: labelList(chartLabel),
      color: colorList(chartLabel),
    },
    {
      data: dataList(ippanLabel),
      label: labelList(ippanLabel),
      color: colorList(ippanLabel),
    },
    {
      data: dataList(seidoLabel),
      label: labelList(seidoLabel),
      color: colorList(seidoLabel),
    },
  ]

  useEffect(() => {
    // flag === 1 <=すでにデータを親から受け取り済み
    // Apiコールでデータを取得する。
    if (flag === 1) {
      ;(async () => {
        store.dispatch(pageLoadRegist(loadingId, true))
        const _data = await APIs.SymbolDetail.SymbolDetail(
          pick(targetData, ["Symbol", "SecurityType", "Exchange"])
        )
        if (!!get(_data, "isAxiosError")) {
          setSymbolData({})
        } else {
          setSymbolData(get(_data, "Symbol"))
        }
        store.dispatch(pageLoadRegist(loadingId, false))
      })()
      return
    }
    // flag === 0 <=データを親からまだ受け取っていない状態。
    // symbolDetailはApiの戻り値なので、初回レンダリング時には空になっている（初回レンダリングとApiのpendding時間は初回レンダリングのほうが早いため）。
    // そのため、flag === 0 かつ　symbolDetailが空でない場合、データを受け取りflagを1にする。
    if (flag === 0) {
      if (isEmpty(targetData)) return
      setSymbolData(targetData)
      setFlag(1)
    }
  }, [props.symbolDetail])

  useEffect(() => {
    isRefresh && store.dispatch(refreshSymbol())
  }, [isRefresh])

  return (
    <div className="panel-division panel-trust">
      <div className="title-table-container">
        <div className="title-contents">
          <div className="title-border">証金残</div>
          <div className="jsf-text">
            <span>JSF</span>
            <span>{judgingKakuhoFlag(get(symbolData, "KakuhoFlag"))}</span>
          </div>
        </div>
        <div className={ClassNames({ "split-division": !isSp })}>
          <div className="panel-splits panel-split-price-col">
            <table className="table table-striped table-border">
              <thead>
                <tr>
                  <th />
                  <th>貸株</th>
                  <th>融資</th>
                  <th>差引</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th className="title-cell">新規</th>
                  <td className="number-cell">
                    {Formatter.number(get(symbolData, "KashikabuShinki"))}
                  </td>
                  <td className="number-cell">
                    {Formatter.number(get(symbolData, "YuushiShinki"))}
                  </td>
                  <td className="number-cell">
                    <span
                      className={Formatter.upDown(
                        get(symbolData, "SashihikiShinki")
                      )}
                    >
                      {Formatter.number(
                        get(symbolData, "SashihikiShinki"),
                        0,
                        "floor",
                        true
                      )}
                    </span>
                  </td>
                </tr>
                <tr>
                  <th className="title-cell">返済</th>
                  <td className="number-cell">
                    {Formatter.number(get(symbolData, "KashikabuHensai"))}
                  </td>
                  <td className="number-cell">
                    {Formatter.number(get(symbolData, "YuushiHensai"))}
                  </td>
                  <td className="number-cell">
                    <span
                      className={Formatter.upDown(
                        get(symbolData, "SashihikiHensai")
                      )}
                    >
                      {Formatter.number(
                        get(symbolData, "SashihikiHensai"),
                        0,
                        "floor",
                        true
                      )}
                    </span>
                  </td>
                </tr>
                <tr>
                  <th className="title-cell">残高</th>
                  <td className="number-cell">
                    {Formatter.number(get(symbolData, "KashikabuZan"))}
                  </td>
                  <td className="number-cell">
                    {Formatter.number(get(symbolData, "YuushiZan"))}
                  </td>
                  <td className="number-cell">
                    <span
                      className={Formatter.upDown(
                        get(symbolData, "SashihikiZan")
                      )}
                    >
                      {Formatter.number(
                        get(symbolData, "SashihikiZan"),
                        0,
                        "floor",
                        true
                      )}
                    </span>
                  </td>
                </tr>
                <tr className="border-row">
                  <th className="title-cell">前日比</th>
                  <td className="number-cell">
                    <span
                      className={Formatter.upDown(
                        get(symbolData, "KashikabuZanZenhi")
                      )}
                    >
                      {Formatter.number(
                        get(symbolData, "KashikabuZanZenhi"),
                        0,
                        "floor",
                        true
                      )}
                    </span>
                  </td>
                  <td className="number-cell">
                    <span
                      className={Formatter.upDown(
                        get(symbolData, "YuushiZanZenhi")
                      )}
                    >
                      {Formatter.number(
                        get(symbolData, "YuushiZanZenhi"),
                        0,
                        "floor",
                        true
                      )}
                    </span>
                  </td>
                  <td className="number-cell">
                    <span
                      className={Formatter.upDown(
                        get(symbolData, "SashihikiZanZenhi")
                      )}
                    >
                      {Formatter.number(
                        get(symbolData, "SashihikiZanZenhi"),
                        0,
                        "floor",
                        true
                      )}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="panel-splits panel-split-price-col">
            <RowTable
              list={rowTable.credit}
              tableCls="table-striped table-border"
              data={symbolData}
            />
          </div>
        </div>
      </div>
      <div className="title-table-container">
        <div className="title-border">信用残</div>
        <div className="split-division">
          <table className="table table-striped table-border">
            <thead>
              <tr>
                <th className="title-cell">
                  {Formatter.date(get(symbolData, "CreatedTime"), "MM/DD")}
                </th>
                <th className="number-cell">合算</th>
                <th className="number-cell">一般</th>
                <th className="number-cell">制度</th>
              </tr>
            </thead>
            <tbody>
              <tr className="graph">
                <td className="title-cell">
                  <div className="market-statistics-labels">
                    {chartLabel.map((lst, i) => (
                      <div key={i} className="market-statistics-label-item">
                        <span
                          className="market-statistics-label-color"
                          style={{ backgroundColor: lst.color }}
                        />
                        {lst.name}
                      </div>
                    ))}
                  </div>
                </td>
                {chartInfo.map((lst, i) => (
                  <td key={i} className="number-cell">
                    <PieChart
                      data={lst.data}
                      labels={lst.label}
                      color={lst.color}
                      marketLength={3}
                    />
                  </td>
                ))}
              </tr>
              <tr className="sell">
                <th className="title-cell down">売残</th>
                <td className="number-cell">
                  <span>
                    {Formatter.number(get(symbolData, "GoukeiUriZan"))}
                  </span>
                  <span
                    className={Formatter.upDown(
                      get(symbolData, "GoukeiUriZanZenhi")
                    )}
                  >
                    (
                    {Formatter.number(
                      get(symbolData, "GoukeiUriZanZenhi"),
                      0,
                      "floor",
                      true
                    )}
                    )
                  </span>
                </td>
                <td className="number-cell">
                  <span>
                    {Formatter.number(get(symbolData, "IppanUriZan"))}
                  </span>
                  <span
                    className={Formatter.upDown(
                      get(symbolData, "IppanUriZanZenhi")
                    )}
                  >
                    (
                    {Formatter.number(
                      get(symbolData, "IppanUriZanZenhi"),
                      0,
                      "floor",
                      true
                    )}
                    )
                  </span>
                </td>
                <td className="number-cell">
                  <span>
                    {Formatter.number(get(symbolData, "SeidoUriZan"))}
                  </span>
                  <span
                    className={Formatter.upDown(
                      get(symbolData, "SeidoUriZanZenhi")
                    )}
                  >
                    (
                    {Formatter.number(
                      get(symbolData, "SeidoUriZanZenhi"),
                      0,
                      "floor",
                      true
                    )}
                    )
                  </span>
                </td>
              </tr>
              <tr className="buy">
                <th className="title-cell up">買残</th>
                <td className="number-cell">
                  <span>
                    {Formatter.number(get(symbolData, "GoukeiKaiZan"))}
                  </span>
                  <span
                    className={Formatter.upDown(
                      get(symbolData, "GoukeiKaiZanZenhi")
                    )}
                  >
                    (
                    {Formatter.number(
                      get(symbolData, "GoukeiKaiZanZenhi"),
                      0,
                      "floor",
                      true
                    )}
                    )
                  </span>
                </td>
                <td className="number-cell">
                  <span>
                    {Formatter.number(get(symbolData, "IppanKaiZan"))}
                  </span>
                  <span
                    className={Formatter.upDown(get(symbolData, "IppanKaiZan"))}
                  >
                    (
                    {Formatter.number(
                      get(symbolData, "IppanKaiZan"),
                      0,
                      "floor",
                      true
                    )}
                    )
                  </span>
                </td>
                <td className="number-cell">
                  <span>
                    {Formatter.number(get(symbolData, "SeidoKaiZan"))}
                  </span>
                  <span
                    className={Formatter.upDown(
                      get(symbolData, "SeidoKaiZanZenhi")
                    )}
                  >
                    (
                    {Formatter.number(
                      get(symbolData, "SeidoKaiZanZenhi"),
                      0,
                      "floor",
                      true
                    )}
                    )
                  </span>
                </td>
              </tr>
              <tr className="margin">
                <th className="title-cell">倍率</th>
                <td className="number-cell">
                  {Formatter.number(get(symbolData, "GoukeiShinyoBairitsu"), 2)}
                  <span className="unit">倍</span>
                </td>
                <td className="number-cell">
                  {Formatter.number(get(symbolData, "IppanShinyoBairitsu"), 2)}
                  <span className="unit">倍</span>
                </td>
                <td className="number-cell">
                  {Formatter.number(get(symbolData, "SeidoShinyoBairitsu"), 2)}
                  <span className="unit">倍</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
